// Platform: web (Chrome, Edge, Firefox), macOS Safari, iOS 16.4+ PWA, Android Chrome
import app from './firebase';
import client from './api/client';
import { registerPushServiceWorker } from './utils/pushServiceWorker';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getIOSVersion() {
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (!match) return { major: 0, minor: 0 };
  return { major: parseInt(match[1], 10), minor: parseInt(match[2], 10) };
}

function onIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function inStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Lazily cached messaging instance
let _messaging = null;

// The registration belongs to whoever is signed in, so a different user or tenant on the same device must register again
const currentOwner = () => `${localStorage.getItem('userId') || ''}:${localStorage.getItem('currentTenantId') || ''}`;

// ---------------------------------------------------------------------------
// Exported helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when this device/browser can receive FCM web-push messages.
 * iOS Safari is only supported in installed PWA mode on iOS 16.4+.
 */
export function isSupported() {
  if (typeof Notification === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;

  if (onIOS()) {
    const v = getIOSVersion();
    const meetsVersion = v.major > 16 || (v.major === 16 && v.minor >= 4);
    return meetsVersion && inStandaloneMode();
  }

  return true;
}

/**
 * Returns true when on iOS Safari but NOT installed as a PWA yet.
 * Use this to show the "Add to Home Screen" banner.
 */
export function needsIOSInstall() {
  return onIOS() && !inStandaloneMode();
}

/**
 * Returns the app service worker registration (push is handled by sw.js)
 * once it is active.
 */
export async function registerServiceWorker() {
  const reg = await registerPushServiceWorker();
  await navigator.serviceWorker.ready;
  return reg;
}

/**
 * Returns the Firebase Messaging instance (singleton).
 */
export function getMessagingInstance() {
  if (_messaging) return _messaging;
  const { getMessaging } = require('firebase/messaging');
  _messaging = getMessaging(app);
  return _messaging;
}

/**
 * Requests a push subscription, saves the FCM token to the backend, and
 * caches it in localStorage.  Returns the token string, or null on failure.
 */
export async function requestAndSaveToken() {
  try {
    const swReg = await registerServiceWorker();
    const messaging = getMessagingInstance();

    const { getToken } = require('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_VAPID_KEY, // TODO: set REACT_APP_VAPID_KEY in .env
      serviceWorkerRegistration: swReg,
    });

    if (!token) return null;

    const stored = localStorage.getItem('fcm_token');
    if (token !== stored || localStorage.getItem('fcm_owner') !== currentOwner()) {
      await client.post('/notifications/register-token', {
        token,
        platform: 'web',
        userAgent: navigator.userAgent,
      });
      localStorage.setItem('fcm_token', token);
      localStorage.setItem('fcm_owner', currentOwner());
    }

    localStorage.setItem('fcm_meta', JSON.stringify({ token, savedAt: Date.now() }));
    localStorage.removeItem('fcm_blocked');
    return token;
  } catch (err) {
    if (err?.code === 'messaging/permission-blocked') {
      localStorage.setItem('fcm_blocked', '1');
    }
    console.warn('[FCM] requestAndSaveToken error:', err?.code ?? err?.message);
    return null;
  }
}

/**
 * Refreshes the token if it is older than 25 days or if Firebase has rotated it.
 * Returns the current valid token, or null.
 */
export async function refreshTokenIfNeeded() {
  try {
    const raw = localStorage.getItem('fcm_meta');
    if (!raw) return null;

    const meta = JSON.parse(raw);
    const TWENTY_FIVE_DAYS = 25 * 24 * 60 * 60 * 1000;
    const stale = Date.now() - meta.savedAt > TWENTY_FIVE_DAYS;

    if (stale) return requestAndSaveToken();

    // Check whether Firebase has silently rotated the subscription.
    const swReg = await registerServiceWorker();
    const messaging = getMessagingInstance();
    const { getToken } = require('firebase/messaging');
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_VAPID_KEY, // TODO: set REACT_APP_VAPID_KEY in .env
      serviceWorkerRegistration: swReg,
    });

    if (!currentToken) return null;

    if (currentToken !== meta.token) {
      await client.put('/notifications/update-token', { oldToken: meta.token, newToken: currentToken });
      localStorage.setItem('fcm_token', currentToken);
      localStorage.setItem('fcm_meta', JSON.stringify({ token: currentToken, savedAt: Date.now() }));
    }

    return currentToken;
  } catch (err) {
    console.warn('[FCM] refreshTokenIfNeeded error:', err?.code ?? err?.message);
    return null;
  }
}

/**
 * Unsubscribes from push, notifies the backend, and clears local state.
 */
export async function deleteToken() {
  try {
    const messaging = getMessagingInstance();
    const { deleteToken: firebaseDeleteToken } = require('firebase/messaging');
    await firebaseDeleteToken(messaging);

    const token = localStorage.getItem('fcm_token');
    if (token) {
      await client.delete('/notifications/token', { data: { token } });
    }
  } catch (err) {
    console.warn('[FCM] deleteToken error:', err?.code ?? err?.message);
  } finally {
    localStorage.removeItem('fcm_token');
    localStorage.removeItem('fcm_meta');
    localStorage.removeItem('fcm_blocked');
    localStorage.removeItem('fcm_owner');
  }
}

/**
 * Listens for foreground (tab-open) messages and calls onMessage(payload).
 * Returns the Firebase unsubscribe function.
 */
export function listenForeground(onMessageCallback) {
  const messaging = getMessagingInstance();
  const { onMessage } = require('firebase/messaging');
  return onMessage(messaging, (payload) => onMessageCallback(payload));
}
